/**
 * The last thing between a person and a message they cannot recall.
 *
 * Every manual send in the CRM opens this first: who it goes to, who it comes
 * from, the subject, and the body as it will actually be rendered. The point is
 * not ceremony - it is that the three things people get wrong (the recipient,
 * the recipient's recipient in a cc, and which of several drafts is in the box)
 * are all invisible in a compose form and all obvious in a preview.
 *
 * It also reports what TEST MODE will do with these particular addresses, and it
 * asks the server rather than working it out here. A dialog that promises "goes
 * to the client" while the guard redirects it - or the reverse - is worse than
 * no dialog, because the operator learns to trust a sentence that nothing
 * checks. One request per dialog open buys that.
 *
 * The check is not a gate: it can fail, and the send button stays available when
 * it does. Test mode is a net under the send, not a permission for it, and a
 * dialog that refuses to send because a status request timed out would be its
 * own outage.
 */
import { useEffect, useState } from "react";
import { AlertTriangle, CalendarClock, FlaskConical, Loader2, Paperclip, Send } from "lucide-react";
import { useI18n, useT } from "../../i18n/I18nProvider";
import { checkMailGuard, type MailGuardCheck } from "../../lib/mail";
import { sanitizeMailHtml } from "../sections/email/mailHtml";
import { formatSendTime } from "../sections/email/drafts/draftTime";
import { Btn, Modal } from "../ui";

export interface SendPreview {
  /** The sending identity, as the recipient will see it. */
  from?: string;
  to: string[];
  cc?: string[];
  /** Blind copies. Shown here because this dialog is the last place anyone
   *  sees them: no recipient's copy of the letter names them. */
  bcc?: string[];
  subject: string;
  /** The rendered body. When absent, `bodyText` is shown with its line breaks. */
  bodyHtml?: string;
  bodyText?: string;
  /** File names travelling with the message. */
  attachments?: string[];
  /**
   * One line for a message whose body the SERVER composes - a sales document's
   * covering letter, a signature request. Says what is being sent when the exact
   * wording is not ours to show.
   */
  note?: string;
  /**
   * Set when the letter is being scheduled rather than sent: the moment it will
   * go. The dialog then asks to schedule, not to send - who it goes to still
   * has to be read before anything is committed to.
   */
  scheduledAt?: string;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3 py-1.5 text-[13px]">
      <span className="w-[86px] shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 flex-1 break-words">{children}</span>
    </div>
  );
}

export function SendConfirmModal({
  preview,
  busy,
  onCancel,
  onConfirm,
}: {
  preview: SendPreview;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const [check, setCheck] = useState<MailGuardCheck | null>(null);

  const to = preview.to.join(", ");
  const cc = (preview.cc ?? []).join(", ");
  const bcc = (preview.bcc ?? []).join(", ");

  useEffect(() => {
    let live = true;
    // The blind copies are asked about too. A client on the Bcc line is the
    // one test mode would otherwise let through with nothing on screen.
    checkMailGuard(preview.to, preview.cc, preview.bcc)
      .then((r) => { if (live) setCheck(r); })
      .catch(() => { if (live) setCheck(null); });
    return () => { live = false; };
    // The addresses are the whole input; joining them keeps a new array
    // identity on every render from refiring the request.
  }, [to, cc, bcc]);

  const guardTone =
    check?.outcome === "blocked" ? "bad"
      : check?.outcome === "redirect" ? "warn"
        : null;

  return (
    <Modal
      title={t("sendConfirm.title")}
      onClose={onCancel}
      /* `.crm-modal` is `width` (not max-width) and scrolls as one block, so a
         long preview pushed the Send button below the fold on a 720px screen.
         The utility layer lands after the component layer, so turning this one
         dialog into a flex column lets the middle scroll and pins the footer,
         without changing the shared class every other dialog wears. */
      className="flex w-[min(680px,100%)] flex-col"
      footer={
        <>
          <Btn onClick={onCancel} disabled={busy}>
            {t("sendConfirm.cancel")}
          </Btn>
          <Btn tone="dark" onClick={onConfirm} disabled={busy}>
            {busy ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : preview.scheduledAt ? (
              <CalendarClock className="size-3.5" />
            ) : (
              <Send className="size-3.5" />
            )}
            {preview.scheduledAt
              ? t("drafts.confirmSchedule")
              : check?.outcome === "redirect"
                ? t("sendConfirm.confirmRedirected")
                : t("sendConfirm.confirm")}
          </Btn>
        </>
      }
    >
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-[26px] py-4">
        {guardTone ? (
          <div
            className="flex items-start gap-2.5 rounded-[18px] px-4 py-3 text-[12.75px] leading-relaxed"
            style={{
              background:
                guardTone === "bad"
                  ? "color-mix(in oklab, var(--color-destructive) 11%, var(--crm-surface))"
                  : "color-mix(in oklab, var(--color-warning) 14%, var(--crm-surface))",
              color:
                guardTone === "bad"
                  ? "var(--color-destructive)"
                  : "color-mix(in oklab, var(--color-warning) 52%, var(--color-foreground))",
            }}
            role="status"
          >
            {guardTone === "bad" ? <AlertTriangle className="mt-0.5 size-4 shrink-0" /> : <FlaskConical className="mt-0.5 size-4 shrink-0" />}
            <span className="min-w-0 flex-1">
              {check?.outcome === "blocked"
                ? t("sendConfirm.guardBlocked")
                : t("sendConfirm.guardRedirect", { inbox: check?.redirectTo ?? "" })}
            </span>
          </div>
        ) : null}

        <div className="rounded-[18px] border border-border px-4 py-2">
          {preview.scheduledAt ? (
            <Row label={t("drafts.goesLabel")}>
              <b className="font-[650]">{formatSendTime(preview.scheduledAt, locale)}</b>
            </Row>
          ) : null}
          {preview.from ? <Row label={t("sendConfirm.from")}>{preview.from}</Row> : null}
          <Row label={t("sendConfirm.to")}>{to || <span className="text-muted-foreground">-</span>}</Row>
          {cc ? <Row label={t("sendConfirm.cc")}>{cc}</Row> : null}
          {bcc ? <Row label={t("sendConfirm.bcc")}>{bcc}</Row> : null}
          <Row label={t("sendConfirm.subject")}>{preview.subject}</Row>
        </div>

        {preview.note ? <p className="crm-sub text-[12.5px]">{preview.note}</p> : null}

        <div>
          <div className="crm-lab !mt-0 uppercase tracking-[0.07em]">{t("sendConfirm.body")}</div>
          {/* No scroll of its own: the whole content column already scrolls, and
              a box inside a scroller is two bars fighting over one wheel. */}
          <div className="rounded-[18px] border border-border bg-[var(--crm-sunk)] px-4 py-3">
            {preview.bodyHtml ? (
              // `crm-mail-html` for the same reason the reading pane uses it: a
              // letter is laid out for a 600px column, and this modal is not one.
              // Trusted, because what is previewed here is what we are about to
              // send - our own HTML, with the inline styling that carries it.
              <div
                className="crm-mail-html text-[13px]"
                dangerouslySetInnerHTML={{ __html: sanitizeMailHtml(preview.bodyHtml, true) }}
              />
            ) : (
              <p className="whitespace-pre-wrap text-[13px]">{preview.bodyText ?? ""}</p>
            )}
          </div>
        </div>

        {preview.attachments?.length ? (
          <div className="flex flex-wrap gap-2">
            {preview.attachments.map((name) => (
              <span
                key={name}
                className="flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs text-muted-foreground"
              >
                <Paperclip className="size-3" /> {name}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
